import styled from "@emotion/styled";
import {
  TextField,
  outlinedInputClasses,
  inputBaseClasses,
  selectClasses,
  buttonClasses,
  autocompleteClasses,
  formHelperTextClasses,
  Box,
} from "@mui/material";

export const styledTextFieldClasses = {
  copyIcon: "copy-icon"
};

const getColors = (theme) => ({
  default: {
    border: theme.palette.system[200],
    background: theme.palette.common.white,
    text: theme.palette.text.primary,
    placeholder: theme.palette.text[600]
  },
  readOnly: {
    border: theme.palette.system[200],
    background: theme.palette.grey[100],
    text: theme.palette.text[800]
  },
  hover: {
    border: theme.palette.grey[400],
  },
  focused: {
    border: theme.palette.primary[500],
  },
  error: {
    border: theme.palette.error[500]
  }
});

export const StyledTextField = styled(TextField, {

  shouldForwardProp: prop => prop !== "isFieldReadOnlyFromSpec"
    && prop !== "showPlayerButton"
    && prop !== "isMultiLine"
    && prop !== "fieldType"
    && prop !== "onAddButtonClick"
    && prop !== "shouldShowAddButton"
    && prop !== "currentSelectedLanguage"
    && prop !== "shouldShowLanguageFlag"
    && prop !== "fetchingRefs"
    && prop !== "popperErrorPlacement"
    && prop !== "showErrorInPopper"
    && prop !== "showAllChips"
    && prop !== "showTopSection"
    && prop !== "wrapperSx"
    && prop !== "wrapperTooltip"
    && prop !== "isDrawerView"
    && prop !== "collectionLanguages"
    && prop !== "showCopyButton"
    && prop !== "testId"
    && prop !== "languageFlagPosition"
    && prop !== "isReadOnly"
    && prop !== "allowTextCursor"
    && prop !== "showErrorMessage"
    && prop !== "isMultiLineSelect"
    && prop !== "forceActionPanel"
    && prop !== "fieldKey"
})`
  .${formHelperTextClasses.root} {
    margin-top: 4px;
  }
    
  .${outlinedInputClasses.root} {
    border-radius: 8px;
    background: ${({ theme, isFieldReadOnlyFromSpec }) => {
      const colors = getColors(theme);
      return isFieldReadOnlyFromSpec ? colors.readOnly.background : colors.default.background;
    }};
    line-height: 24px;
    padding: 12px;
    ${({ multiline }) => multiline && "height: auto;"}
    box-shadow: ${({ theme }) => theme.shadows[1]};
    color: ${({ theme }) => getColors(theme).default.text};

    ${({ readOnly }) => readOnly && `
      & .${autocompleteClasses.endAdornment} {
        pointer-events: none;
      }
    `}
    & fieldset {
      border: 1px solid ${({ theme }) => getColors(theme).default.border};
      ${({ isReadOnly, readOnly, disabled, theme }) => (isReadOnly || readOnly || disabled) && `border-color: ${getColors(theme).readOnly.border} !important;`}
      ${({ isFieldReadOnlyFromSpec }) => isFieldReadOnlyFromSpec && "border: none !important;"}
    }

    &:hover fieldset {
      border: 1px solid ${({ theme, disabled, readOnly, isFieldReadOnlyFromSpec, error }) => {
        if (isFieldReadOnlyFromSpec) {
          return "transparent;";
        }
        if (disabled || readOnly) {
          return getColors(theme).default.border;
        }
        if (error) {
          return `${getColors(theme).error.border}`;
        }
        return getColors(theme).hover.border;
      }} !important;
      ${({ isFieldReadOnlyFromSpec }) => isFieldReadOnlyFromSpec && "border: none; !important;"}
    }

    &.${outlinedInputClasses.focused} fieldset {
      border:${({ theme, isFieldReadOnlyFromSpec, disabled, readOnly, error }) => {
        if (isFieldReadOnlyFromSpec || disabled || readOnly) {
          return `1px solid ${getColors(theme).default.border}`;
        }
        if (error) {
          return `2px solid ${getColors(theme).error.border}`;
        }
        return `2px solid ${getColors(theme).focused.border}`;
      }} !important;
    }
    
    &.${inputBaseClasses.root}.Mui-disabled textarea,
    &.${inputBaseClasses.root}.Mui-disabled input {
      color: ${({ theme }) => getColors(theme).readOnly.text};
      -webkit-text-fill-color: unset;
    }

    .${outlinedInputClasses.input} {
      padding: 0;
      line-height: 24px;
      height: ${({ isMultiLineSelect, multiline }) => (isMultiLineSelect || multiline) ? "auto" : "24px"};
      cursor: default;
      
      &:hover {
        cursor: text;
      }
      
      &::placeholder {
        color: ${({ theme }) => getColors(theme).default.placeholder};
        opacity: 1;
      }
    }
  }

  ${({ allowTextCursor }) => !allowTextCursor && `
    .${outlinedInputClasses.root} {
      cursor: default;
    }
    .${outlinedInputClasses.input} {
      cursor: default !important;
    }
  `}

  ${({ readOnly }) =>
    readOnly &&
    `
    .${outlinedInputClasses.root} {
      cursor: default;
      &:hover {
        cursor: default !important;
      }
    }
    .${outlinedInputClasses.input} {
      cursor: default;
      &:hover {
        cursor: default !important;
      }
    }
    & .${autocompleteClasses.endAdornment} .${buttonClasses.root} {
      pointer-events: none;
    }
  `}

  .${styledTextFieldClasses.copyIcon} {
    opacity: ${({ forceActionPanel }) => forceActionPanel ? "1" : "0"};
    pointer-events: ${({ forceActionPanel }) => forceActionPanel ? "auto" : "none"};
  }
  &:hover .${styledTextFieldClasses.copyIcon} {
    opacity: 1;
    pointer-events: auto;
  }
  &.Mui-focused .${styledTextFieldClasses.copyIcon},
  & .Mui-focused .${styledTextFieldClasses.copyIcon} {
    opacity: 0;
    pointer-events: none;
  }
`;

export const ActionPanelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: theme.palette.system[0],
  boxShadow: theme.shadows[3],
  borderRadius: "8px",
  padding: 6
}));
